import { useEffect, useRef, useState } from 'react';
import { swatchEdgeColor, type AssetRef } from '@motorcity/shared';
import { getConfig } from '../config';

/**
 * Eye-dropper over an uploaded photograph.
 *
 * Sampling the real bodywork beats typing a hex by hand: the chip in the app
 * then matches the photography the customer is looking at, rather than a guess
 * at what "Misty Cyan" means.
 *
 * This works because the `/assets/*` CloudFront behaviour returns
 * `Access-Control-Allow-Origin: *`. Without that header the canvas would be
 * tainted and `getImageData` would throw a SecurityError.
 */
export function ColorPicker({
  asset,
  value,
  onPick,
  onClose,
}: {
  asset: AssetRef;
  value: string;
  onPick: (hex: string) => void;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [hover, setHover] = useState<string | undefined>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const image = new Image();
    // Must be set before `src`, or the browser caches a non-CORS response and
    // the canvas is tainted on every subsequent load.
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const maxWidth = 640;
      const scale = Math.min(1, maxWidth / image.naturalWidth);
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
      setReady(true);
    };
    image.onerror = () =>
      setError('Could not load the image for sampling. Enter a hex value instead.');
    image.src = `${getConfig().contentBaseUrl}/${asset.key}`;
  }, [asset.key]);

  function sample(event: React.MouseEvent<HTMLCanvasElement>): string | undefined {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { willReadFrequently: true });
    if (!canvas || !context) return undefined;

    // The canvas is CSS-scaled to fit the dialog, so map click coordinates back
    // into the bitmap's own pixel space.
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor(((event.clientX - rect.left) / rect.width) * canvas.width);
    const y = Math.floor(((event.clientY - rect.top) / rect.height) * canvas.height);

    try {
      const [r, g, b] = context.getImageData(x, y, 1, 1).data;
      return `#${[r, g, b].map((c) => (c ?? 0).toString(16).padStart(2, '0')).join('')}`;
    } catch {
      setError('The browser blocked reading this image. Enter a hex value instead.');
      return undefined;
    }
  }

  return (
    <div className="picker-backdrop" role="dialog" aria-modal="true" aria-label="Pick a colour">
      <div className="picker">
        <div className="picker-head">
          <strong>Click the car to sample its colour</strong>
          <button type="button" className="btn subtle" onClick={onClose}>
            Close
          </button>
        </div>

        {error ? <p className="field-error">{error}</p> : null}

        <div className="picker-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="picker-canvas"
            onMouseMove={(e) => setHover(sample(e))}
            onMouseLeave={() => setHover(undefined)}
            onClick={(e) => {
              const hex = sample(e);
              if (hex) {
                onPick(hex);
                onClose();
              }
            }}
          />
          {!ready && !error ? <p className="loading">Loading image…</p> : null}
        </div>

        <div className="picker-foot">
          <span
            className="swatch lg"
            style={{
              background: hover ?? value,
              borderColor: swatchEdgeColor(hover ?? value),
            }}
          />
          <code>{hover ?? value}</code>
          <span className="field-hint">
            {hover ? 'Click to use this colour' : 'Hover the image to preview'}
          </span>
        </div>
      </div>
    </div>
  );
}
